import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getPyg } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { ChevronDown, ChevronRight } from "lucide-react";
import useModoAsiento, { CodigoCuenta } from "@/lib/useModoAsiento";

export default function PygContent({ ejercicioId }) {
  const [pyg, setPyg] = useState(null);
  const [loading, setLoading] = useState(true);
  // Navegación tipo SAP: con diario (HOLDED*), cada cuenta salta a su mayor.
  const { modoAsiento, irAlMayor } = useModoAsiento(ejercicioId);

  useEffect(() => {
    loadPyg();
  }, [ejercicioId]);

  const loadPyg = async () => {
    setLoading(true);
    try {
      const response = await getPyg(ejercicioId);
      setPyg(response.data);
    } catch (error) {
      toast.error("Error al cargar PyG");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* PyG Card */}
      <Card>
        <CardContent className="pt-6">
          {/* Sección de Explotación: Partidas 1-12 */}
          <div className="space-y-1">
            {pyg?.partidas?.filter(p => {
              const num = parseInt(p.partida);
              return num >= 1 && num <= 12;
            }).map((partida, index) => (
              <PygPartida key={index} partida={partida} onVerMayor={modoAsiento ? irAlMayor : null} />
            ))}
          </div>

          {/* A.1) Resultado de Explotación */}
          <div className="flex justify-between items-center p-3 bg-muted rounded-lg my-4">
            <span className="font-semibold">A.1) RESULTADO DE EXPLOTACIÓN</span>
            <span className={`font-mono font-bold text-lg ${pyg?.resultado_explotacion >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {formatCurrency(pyg?.resultado_explotacion)}
            </span>
          </div>

          {/* Sección Financiera: Partidas 13-17 */}
          <div className="space-y-1">
            {pyg?.partidas?.filter(p => {
              const num = parseInt(p.partida);
              return num >= 13 && num <= 17;
            }).map((partida, index) => (
              <PygPartida key={index} partida={partida} onVerMayor={modoAsiento ? irAlMayor : null} />
            ))}
          </div>

          {/* A.2) Resultado Financiero */}
          <div className="flex justify-between items-center p-3 bg-muted rounded-lg my-4">
            <span className="font-semibold">A.2) RESULTADO FINANCIERO</span>
            <span className={`font-mono font-bold text-lg ${pyg?.resultado_financiero >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {formatCurrency(pyg?.resultado_financiero)}
            </span>
          </div>

          {/* A.3) Resultado Antes de Impuestos */}
          <div className="flex justify-between items-center p-3 bg-muted rounded-lg mb-4">
            <span className="font-semibold">A.3) RESULTADO ANTES DE IMPUESTOS</span>
            <span className={`font-mono font-bold text-lg ${pyg?.resultado_antes_impuestos >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {formatCurrency(pyg?.resultado_antes_impuestos)}
            </span>
          </div>

          {/* Sección Impuestos: Partida 18 */}
          <div className="space-y-1">
            {pyg?.partidas?.filter(p => {
              const num = parseInt(p.partida);
              return num === 18;
            }).map((partida, index) => (
              <PygPartida key={index} partida={partida} onVerMayor={modoAsiento ? irAlMayor : null} />
            ))}
          </div>

          {/* A.4) Resultado del Ejercicio */}
          <div className="flex justify-between items-center p-3 bg-primary text-primary-foreground rounded-lg mt-4">
            <span className="font-bold">A.4) RESULTADO DEL EJERCICIO</span>
            <span className="font-mono font-bold text-xl">
              {formatCurrency(pyg?.resultado_ejercicio)}
            </span>
          </div>

          <Separator className="my-4" />

          {/* EBITDA */}
          <div className="flex justify-between items-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <div>
              <span className="font-semibold text-blue-600 dark:text-blue-400">EBITDA</span>
              <p className="text-xs text-muted-foreground">Resultado de explotación + Amortizaciones</p>
            </div>
            <span className={`font-mono font-bold text-lg ${pyg?.ebitda >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-rose-600'}`}>
              {formatCurrency(pyg?.ebitda)}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PygPartida({ partida, onVerMayor }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasCuentas = partida.cuentas && partida.cuentas.length > 0;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <div className="flex items-center justify-between py-2 px-3 rounded hover:bg-muted/50 cursor-pointer">
          <div className="flex items-center gap-2">
            {hasCuentas && (
              <span className="text-muted-foreground">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </span>
            )}
            {!hasCuentas && <span className="w-4" />}
            <span className="text-sm">{partida.partida}</span>
          </div>
          <span className={`font-mono tabular-nums text-sm ${partida.saldo < 0 ? 'text-rose-600' : partida.saldo > 0 ? 'text-emerald-600' : ''}`}>
            {formatCurrency(partida.saldo)}
          </span>
        </div>
      </CollapsibleTrigger>

      {hasCuentas && (
        <CollapsibleContent>
          <div className="ml-6 mb-2 p-2 bg-muted/30 rounded text-xs">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-8 text-xs">Código</TableHead>
                  <TableHead className="h-8 text-xs">Nombre</TableHead>
                  <TableHead className="h-8 text-xs text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partida.cuentas.map((cuenta, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="py-1 font-mono">
                      <CodigoCuenta codigo={cuenta.codigo} onVerMayor={onVerMayor} />
                    </TableCell>
                    <TableCell className="py-1 max-w-[200px] truncate">{cuenta.nombre}</TableCell>
                    <TableCell className={`py-1 text-right font-mono tabular-nums ${cuenta.saldo < 0 ? 'text-rose-600' : ''}`}>
                      {formatCurrency(cuenta.saldo)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
}
